import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, CONTACT_INFO } from '../data/mock';
import { CheckCircle2, ArrowRight, ShoppingCart, Truck, Shield, Clock, Headphones } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'high-performance-carbon-fiber-ceramic-rear-pulleys') || products[0];
  const productsGrid = [
    products.find(p => p.slug === 'smart-helmet'),
    products.find(p => p.slug === 'urban-chic-front-bike-basket-with-wooden-slats'),
    products.find(p => p.slug === 'wall-mounted-bike-repair-stand-with-adjustable-clamp'),
    products.find(p => p.slug === 'bluetooth-bike-speaker'),
    products.find(p => p.slug === '1-pair-bicycle-rear-view-mirror'),
    products.find(p => p.slug === 'bike-handlebar-bottle-holders'),
    products.find(p => p.slug === 'bike-performance-accessory'),
    products.find(p => p.slug === 'rechargeable-air-pump'),
  ].filter(Boolean);
  const { addToCart } = useCart();

  useEffect(() => {
    document.title = 'EC Pedal Works | Premium Bike Accessories & Cycling Gear';
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Hero Section */}
        <section className="relative h-[560px] md:h-[650px] w-full overflow-hidden">
          <img
            src={siteImages.heroMain}
            alt="Ride in Style"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-l from-orange-700 via-black/40 to-transparent" />

          {/* Centered Content */}
          <div className="relative z-10 mx-auto flex h-full max-w-3xl flex-col items-center justify-center px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">
              Ride in Style
            </h1>
            <p className="mt-4 text-lg md:text-xl text-gray-200 max-w-2xl">
              Advanced bike accessories for every cycling enthusiast.
            </p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-orange-600 px-8 py-3 font-medium text-white transition hover:scale-105 hover:bg-orange-500 focus:ring-4 focus:ring-white/40"
            >
              Shop Bike Gadgets
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Pedal with Confidence Section */}
        <section className="bg-gray-50 py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center bg-white rounded-3xl shadow-sm ring-1 ring-gray-100 px-6 py-12 sm:p-12 lg:p-16">
              <img
                className="h-80 w-full lg:h-auto lg:max-w-sm rounded-2xl object-cover shadow-xl flex-shrink-0"
                src={siteImages.heroSecondary}
                alt="Pedal with Confidence"
              />
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
                  Pedal with Confidence
                </h2>
                <p className="mt-5 text-lg text-gray-600">
                  Gear up with essential bike gadgets for safety and style.
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {[
                    'Safety-first designs',
                    'Enhanced comfort',
                    'Durable and reliable',
                    'Easy installation',
                    'Cyclist-approved',
                    'Guaranteed satisfaction',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-gray-700">
                      <CheckCircle2 className="h-5 w-5 flex-none text-orange-600 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <Link to="/shop" className="text-sm font-semibold text-orange-600 hover:text-orange-500">
                    Shop Bike Gadgets <span aria-hidden="true">&rarr;</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Product + Grid */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Featured Product */}
            {featuredProduct && (
              <div className="flex flex-col lg:flex-row bg-white rounded-xl shadow-lg overflow-hidden mb-12">
                <img
                  className="w-full lg:w-1/2 object-cover h-[280px] sm:h-[360px] lg:h-auto"
                  src={featuredProduct.image}
                  alt={featuredProduct.name}
                />
                <div className="p-6 lg:p-10 flex flex-col justify-between lg:w-1/2">
                  <div>
                    <span className="inline-block text-xs font-semibold uppercase tracking-wider text-orange-600 bg-orange-50 px-3 py-1 rounded-full mb-4">
                      Featured Product
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
                      {featuredProduct.name}
                    </h2>
                    <p className="text-2xl font-semibold text-orange-600 mb-4">
                      ${featuredProduct.price.toFixed(2)}
                    </p>
                    <p className="text-gray-600 mb-6">
                      {featuredProduct.description.slice(0, 180)}...
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/product/${featuredProduct.slug}`}
                      className="inline-flex items-center gap-2 px-5 py-3 bg-orange-600 text-white rounded-lg shadow hover:bg-orange-500 transition font-medium"
                    >
                      Buy Now
                    </Link>
                    <Link
                      to="/shop"
                      className="inline-flex items-center gap-2 px-5 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                    >
                      Browse More
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {productsGrid.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden flex flex-col"
                >
                  <Link to={`/product/${product.slug}`}>
                    <img
                      className="w-full object-cover h-64"
                      src={product.image}
                      alt={product.name}
                    />
                  </Link>
                  <div className="p-4 flex flex-col flex-grow justify-between">
                    <div>
                      <Link
                        to={`/product/${product.slug}`}
                        className="block text-lg font-semibold text-gray-800 hover:text-orange-600 line-clamp-2"
                      >
                        {product.name}
                      </Link>
                      <p className="text-sm text-gray-500 my-2 line-clamp-2">
                        {product.description.slice(0, 70)}...
                      </p>
                    </div>
                    <div>
                      <div className="text-orange-600 font-bold text-lg mb-3">
                        ${product.price.toFixed(2)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => addToCart(product)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 transition"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Add
                        </button>
                        <Link
                          to={`/product/${product.slug}`}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-100 transition"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* View All */}
            <div className="text-center mt-12">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-gray-300 text-gray-800 font-semibold rounded-lg hover:bg-gray-50 transition"
              >
                View All Bike Gear
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                Shop By Category
              </h2>
              <p className="mt-3 text-gray-600 max-w-xl mx-auto">
                Browse our curated collections of premium bike accessories and cycling gear gear.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative h-48 rounded-xl overflow-hidden shadow hover:shadow-xl transition-all bg-gray-900"
                >
                  {category.image && (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="text-lg font-bold text-white">
                      {category.name}
                    </h3>
                    <p className="text-xs text-white/80 mt-0.5">
                      {category.productCount} Product{category.productCount > 1 ? 's' : ''}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-gray-50 py-14 border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Truck, title: 'Free USA Shipping', desc: 'On every order via USPS' },
                { icon: Clock, title: '2-4 Day Delivery', desc: 'Fast nationwide shipping' },
                { icon: Shield, title: 'Secure Checkout', desc: 'Encrypted payments' },
                { icon: Headphones, title: '30-Day Returns', desc: 'No-hassle return policy' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-gray-900 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/5 rounded-3xl ring-1 ring-white/10 px-6 py-14 lg:px-16 lg:py-16 flex flex-col gap-10">
              <h2 className="text-3xl font-bold text-white text-center">
                Frequently Asked Questions
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-gray-300">
                {[
                  {
                    q: 'What is the typical shipping time?',
                    a: `Your order will be processed and shipped via USPS within 1 business day. Delivery is expected between 2 to 4 business days. For delays, contact customer care at ${CONTACT_INFO.phone}.`
                  },
                  {
                    q: 'Is shipping free?',
                    a: 'Yes, all orders include free USPS Standard Shipping within the USA.'
                  },
                  {
                    q: 'What does the 30-day return policy entail?',
                    a: 'You have 30 days from receiving your product to initiate a return. Please contact customer service for assistance.'
                  },
                  {
                    q: 'How to reach customer service?',
                    a: `Reach our customer service team anytime at ${CONTACT_INFO.phone} or via email at ${CONTACT_INFO.email}.`
                  },
                ].map((faq, i) => (
                  <div key={i} className="bg-white/10 rounded-lg p-6 shadow hover:shadow-md transition">
                    <h3 className="text-lg font-semibold text-white">{faq.q}</h3>
                    <p className="mt-2 text-gray-300">{faq.a}</p>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 font-medium"
                >
                  See all FAQs
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
